import React, { useState, useMemo, useRef } from 'react';
import { CheckCircle, ChevronDown, Download, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { resolvePlaybackUrl, resolveThumbnailUrl, useHlsVideo } from '../../utils/hlsPlayer';

export const API = process.env.REACT_APP_BACKEND_URL;

// Admin email whitelist - add your email here
export const ADMIN_EMAILS = ['modtest@aivue.com', 'admin@audivue.com', 'cpowersv@gmail.com'];

/**
 * Admin video preview — supports both legacy MP4 (via /api/files) and Bunny
 * HLS. Uses the shared `useHlsVideo` hook so the .m3u8 case actually plays
 * everywhere (without this, the modal would show a blank black box on Bunny
 * uploads because <video src="...m3u8"> isn't natively supported outside Safari).
 */
export const AdminPreviewVideo = ({ previewVideo }) => {
  const videoRef = useRef(null);
  const playbackUrl = resolvePlaybackUrl(previewVideo);
  const poster = resolveThumbnailUrl(previewVideo);
  useHlsVideo(videoRef, playbackUrl, { autoStart: false });
  return (
    <video
      ref={videoRef}
      key={previewVideo.id}
      poster={poster || undefined}
      controls
      autoPlay
      playsInline
      className="w-full max-h-[70vh] object-contain bg-black"
      data-testid="preview-video-element"
    >
      Sorry, your browser doesn&apos;t support embedded videos.
    </video>
  );
};

export const StatCard = ({ icon: Icon, label, value, change, color = 'orange', onClick, clickable = false }) => (
  <div 
    className={`bg-zinc-900/50 rounded-xl p-5 border border-zinc-800 transition-all ${
      clickable ? 'cursor-pointer hover:bg-zinc-800/70 hover:border-zinc-700 hover:scale-[1.02]' : ''
    }`}
    onClick={clickable ? onClick : undefined}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-500`} />
      </div>
      {change !== undefined && (
        <span className={`text-xs font-medium ${change > 0 ? 'text-green-500' : change < 0 ? 'text-red-500' : 'text-zinc-500'}`}>
          {change > 0 ? '+' : ''}{change}%
        </span>
      )}
      {clickable && (
        <span className="text-xs text-zinc-500">Click to view →</span>
      )}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-zinc-500">{label}</p>
  </div>
);

// Small key/value tile used inside the video diagnostic modal.
export const DiagField = ({ label, value, warn = false }) => (
  <div className="rounded-lg bg-zinc-800/50 border border-zinc-800 px-2.5 py-1.5">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-xs font-medium truncate ${warn ? 'text-amber-300' : 'text-zinc-200'}`}>{value || '—'}</p>
  </div>
);

export const GrowthSparkline = ({ daily }) => {
  if (!Array.isArray(daily) || daily.length === 0) return null;
  const W = 720;
  const H = 140;
  const PAD_L = 28;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 22;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = daily.length;
  const maxVal = Math.max(1, ...daily.map((d) => Math.max(d.signups || 0, d.new_publishers || 0)));
  const xFor = (i) => PAD_L + (n <= 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const yFor = (v) => PAD_T + innerH - (v / maxVal) * innerH;
  const linePath = (key) =>
    daily
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(d[key] || 0).toFixed(1)}`)
      .join(' ');
  const areaPath = (key) => {
    const top = daily
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(d[key] || 0).toFixed(1)}`)
      .join(' ');
    return `${top} L ${xFor(n - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;
  };
  const xLabels = [daily[0].date, daily[Math.floor(n / 2)].date, daily[n - 1].date].map((d) => d.slice(5));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Daily signups and new publishers over the last 30 days" data-testid="growth-funnel-sparkline">
      <defs>
        <linearGradient id="signupsArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(34,211,238)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="rgb(34,211,238)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="publishersArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(232,121,249)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="rgb(232,121,249)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Y gridlines */}
      {[0, 0.5, 1].map((p) => (
        <line
          key={`grid-${p}`}
          x1={PAD_L}
          x2={W - PAD_R}
          y1={PAD_T + innerH * (1 - p)}
          y2={PAD_T + innerH * (1 - p)}
          stroke="rgb(63,63,70)"
          strokeDasharray="2 4"
          strokeWidth="0.5"
        />
      ))}
      <text x={PAD_L - 6} y={PAD_T + 4} textAnchor="end" fontSize="10" fill="rgb(113,113,122)">{maxVal}</text>
      <text x={PAD_L - 6} y={PAD_T + innerH + 4} textAnchor="end" fontSize="10" fill="rgb(113,113,122)">0</text>
      {/* Areas */}
      <path d={areaPath('signups')} fill="url(#signupsArea)" />
      <path d={areaPath('new_publishers')} fill="url(#publishersArea)" />
      {/* Lines */}
      <path d={linePath('signups')} fill="none" stroke="rgb(34,211,238)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <path d={linePath('new_publishers')} fill="none" stroke="rgb(232,121,249)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {/* X labels */}
      <text x={PAD_L} y={H - 6} fontSize="10" fill="rgb(113,113,122)">{xLabels[0]}</text>
      <text x={W / 2} y={H - 6} fontSize="10" fill="rgb(113,113,122)" textAnchor="middle">{xLabels[1]}</text>
      <text x={W - PAD_R} y={H - 6} fontSize="10" fill="rgb(113,113,122)" textAnchor="end">{xLabels[2]}</text>
    </svg>
  );
};

// Single-series cyan area sparkline shown in the new Overview hero card to
// visualise daily unique visitors across the last 30 days. The /admin/visitors
// endpoint returns a sparse array — we zero-fill to a continuous window so
// the line draws across the full range.
export const VisitorSparkline = ({ daily }) => {
  if (!Array.isArray(daily)) daily = [];
  const byDate = {};
  daily.forEach((d) => { if (d && d.date) byDate[d.date] = d; });
  const filled = [];
  for (let i = 29; i >= 0; i--) {
    const dt = new Date();
    dt.setUTCDate(dt.getUTCDate() - i);
    const iso = dt.toISOString().slice(0, 10);
    filled.push(byDate[iso] || { date: iso, uniques: 0, visits: 0 });
  }
  const W = 720;
  const H = 90;
  const PAD_L = 24;
  const PAD_R = 8;
  const PAD_T = 6;
  const PAD_B = 18;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = filled.length;
  const maxVal = Math.max(1, ...filled.map((d) => d.uniques || 0));
  const xFor = (i) => PAD_L + (i * innerW) / (n - 1);
  const yFor = (v) => PAD_T + innerH - (v / maxVal) * innerH;
  const linePath = filled
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(d.uniques || 0).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${xFor(n - 1).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;
  const xLabels = [filled[0].date, filled[Math.floor(n / 2)].date, filled[n - 1].date].map((d) => (d || '').slice(5));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Daily unique visitors over the last 30 days" data-testid="visitor-sparkline">
      <defs>
        <linearGradient id="visitorAreaFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(34,211,238)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="rgb(34,211,238)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <text x={PAD_L - 4} y={PAD_T + 4} textAnchor="end" fontSize="9" fill="rgb(113,113,122)">{maxVal}</text>
      <text x={PAD_L - 4} y={PAD_T + innerH + 4} textAnchor="end" fontSize="9" fill="rgb(113,113,122)">0</text>
      <path d={areaPath} fill="url(#visitorAreaFill)" />
      <path d={linePath} fill="none" stroke="rgb(34,211,238)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <text x={PAD_L} y={H - 4} fontSize="9" fill="rgb(113,113,122)">{xLabels[0]}</text>
      <text x={W / 2} y={H - 4} fontSize="9" fill="rgb(113,113,122)" textAnchor="middle">{xLabels[1]}</text>
      <text x={W - PAD_R} y={H - 4} fontSize="9" fill="rgb(113,113,122)" textAnchor="end">{xLabels[2]}</text>
    </svg>
  );
};

export const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
      active 
        ? 'bg-orange-500 text-white' 
        : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
    {count !== undefined && (
      <span className={`px-2 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : 'bg-zinc-700'}`}>
        {count}
      </span>
    )}
  </button>
);

// ─── Mass-Email Copy Helper ────────────────────────────────────────────
// Lets an admin copy the email column of the currently-filtered Users
// table to the clipboard. Two formats:
//   • Comma-separated → drops cleanly into a Gmail BCC field
//   • One-per-line     → drops cleanly into a spreadsheet column
// Whichever filter is active in the Users tab (search query, status,
// date) is respected — only the visible rows are exported.
export const CopyEmailsButton = ({ users }) => {
  const [open, setOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(null);

  const emails = useMemo(
    () => (users || []).map((u) => u.email).filter(Boolean),
    [users]
  );
  const count = emails.length;

  const copy = async (separator, kind) => {
    if (!count) return;
    const text = emails.join(separator);
    try {
      await navigator.clipboard.writeText(text);
    } catch (_err) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_e) { /* clipboard blocked */ }
      document.body.removeChild(ta);
    }
    setJustCopied(kind);
    setTimeout(() => setJustCopied(null), 1800);
    setOpen(false);
    toast.success(`${count} email${count === 1 ? '' : 's'} copied to clipboard`);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={!count}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="admin-copy-emails-btn"
        title={count ? `Copy ${count} email${count === 1 ? '' : 's'} to clipboard` : 'No users in current filter'}
      >
        {justCopied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        <span className="hidden sm:inline">
          {justCopied ? 'Copied!' : `Copy Emails (${count})`}
        </span>
        <span className="sm:hidden">{justCopied ? 'Copied!' : `Copy ${count}`}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-64 rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl z-50 overflow-hidden"
            data-testid="admin-copy-emails-menu"
          >
            <button
              onClick={() => copy(', ', 'comma')}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-zinc-800 text-left transition-colors"
              data-testid="admin-copy-emails-comma"
            >
              <Mail className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Comma-separated</p>
                <p className="text-xs text-zinc-500 truncate">
                  For Gmail / Outlook BCC fields
                </p>
              </div>
            </button>
            <div className="h-px bg-zinc-800" />
            <button
              onClick={() => copy('\n', 'lines')}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-zinc-800 text-left transition-colors"
              data-testid="admin-copy-emails-lines"
            >
              <Download className="w-4 h-4 mt-0.5 text-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">One per line</p>
                <p className="text-xs text-zinc-500 truncate">
                  For pasting into a spreadsheet
                </p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};
